import { z } from "zod/v4";
import { allowedFurgonetkaProviders } from "@/lib/addons/furgonetka/furgonetka-lib";
import type { Pretty } from "@/lib/types";
import { themeColorsSerializedSchema } from "./admin/store-builder/theme-colors";
import { fontConfigs } from "./fonts";

export const StoreFlagsSchema = z.object({
	verifyPKD: z.boolean().nullish(),
	gold: z.boolean().nullish(),
});
export type StoreFlags = z.infer<typeof StoreFlagsSchema>;

export const ProductFlagsSchema = z.object({
	gold: z.boolean().nullish(),
});
export type ProductFlags = z.infer<typeof ProductFlagsSchema>;

export const SettingsSchema = z.object({
	storeName: z
		.string()
		.nullish()
		.transform((value) => value || ""),
	storeDescription: z.string().nullish().default(""),
	freeShippingThreshold: z
		.any()
		.transform((v) => (typeof v === "number" ? BigInt(v) : null))
		.nullish(),
	pageWidth: z
		// narrow: 1152px
		// default: 1280px
		// wider: 1408px
		// widest: 1536px
		.enum(["narrow", "default", "wider", "widest"])
		.nullish()
		.transform((value) => value || "default"),
	fontFamily: z
		.enum(["default", ...(Object.keys(fontConfigs) as (keyof typeof fontConfigs)[])])
		.nullish()
		.transform((value) => value || "default"),
	logo: z
		.union([
			z
				.object({
					imageUrl: z.string().url().startsWith("http"),
					width: z
						.number()
						.nullish()
						.transform((value) => value || undefined),
					height: z
						.number()
						.nullish()
						.transform((value) => value || undefined),
				})
				.nullish(),

			/**
			 * @deprecated
			 */
			z
				.string()
				.url()
				.nullish()
				.transform((value) => (value ? { imageUrl: value, width: undefined, height: undefined } : null)),
		])
		.nullish(),
	ogimage: z.string().url().startsWith("http").nullish(),
	favicon: z
		.object({
			imageUrl: z.string().url().startsWith("http"),
			width: z
				.number()
				.nullish()
				.transform((value) => value || undefined),
			height: z
				.number()
				.nullish()
				.transform((value) => value || undefined),
		})
		.nullish(),
	colors: z
		.object({
			paletteName: z.string().nullish(),
			palette: themeColorsSerializedSchema,
		})
		.nullish(),
	invoiceSettings: z
		.object({
			template: z.string(),
			cadence: z.enum(["monthly", "yearly"]),
		})
		.nullish(),
	outboundEmail: z.string().email().nullish(),
	notificationEmail: z.string().email().nullish(),
	buttons: z
		.object({
			borderWidth: z.number().min(0).max(3).nullish(),
			borderRadius: z
				.enum(["none", "sm", "md", "lg", "full"])
				.nullish()
				.transform((value) => value || "md"),
			shadow: z
				.enum(["none", "sm", "md", "lg"])
				.nullish()
				.transform((value) => value || "none"),
			colors: z
				.object({
					background: z.string().nullish(),
					text: z.string().nullish(),
					backgroundHover: z.string().nullish(),
					border: z.string().nullish(),
				})
				.nullish(),
		})
		.nullish(),
	cartIcon: z
		.enum(["shopping-bag", "shopping-cart", "shopping-basket"])
		.nullish()
		.transform((value) => value || "shopping-bag"),
});

export type Settings = z.output<typeof SettingsSchema>;
export type SettingsInput = z.input<typeof SettingsSchema>;
export type SettingsKeys = Pretty<keyof Settings>;
export const settingsAllowedFields = Object.keys(SettingsSchema.shape) as ["storeName", ...SettingsKeys[]];

export const BadgeSchema = z.object({
	content: z.string().optional(),
	backgroundColor: z.string().optional(),
	textColor: z.string().optional(),
});
export type Badge = z.infer<typeof BadgeSchema>;

export const AttributesSchema = z.array(
	z.object({ checked: z.boolean().nullish().default(false), key: z.string(), value: z.string() }),
);

export const ShippingAddonDataSchema = z.object({
	id: z.number(),
	name: z.string(),
	service: z.enum(allowedFurgonetkaProviders),
});
export type ShippingAddonData = z.infer<typeof ShippingAddonDataSchema>;
